<template>
<div>
  <ItpFormGroup
    name="rental_fee_advances_initial_months"
    :label="'Initial advances'|pgettext('Contract')"
    :appendText="'months'|pgettext('InputAddon')"
    labelColsMd="4"
    contentColsMd="4"
    :labelAlign="labelAlign"
  >
    <ItpFormInput
      type="number"
      name="rental_fee_advances_initial_months"
      v-model="contract.invoicing.rental_fee_advances_initial_months"
    >
    </ItpFormInput>
  </ItpFormGroup>
  <ItpFormGroup
    name="rental_fee_advances_reserve_months"
    :label="'Reserve advances'|pgettext('Contract')"
    :appendText="'months'|pgettext('InputAddon')"
    labelColsMd="4"
    contentColsMd="4"
    :labelAlign="labelAlign"
  >
    <ItpFormInput
      type="number"
      name="rental_fee_advances_reserve_months"
      v-model="contract.invoicing.rental_fee_advances_reserve_months"
    >
    </ItpFormInput>
  </ItpFormGroup>
  <hr>
  </hr>
  <ItpFormGroup
    name="billing_currency"
    :label="'Billing currency'|pgettext('Contract')"
    labelColsMd="4"
    contentColsMd="3"
    :labelAlign="labelAlign"
  >
    <ItpFormSelect2
      name="billing_currency"
      valueField="code"
      textField="code"
      required
      v-model="contract.invoicing.billing_currency"
      :options="currencies"
    >
    </ItpFormSelect2>
  </ItpFormGroup>
  <ItpFormGroup
    name="billing_frequency"
    :label="'Billing frequency'|pgettext('Contract')"
    labelColsMd="4"
    contentColsMd="6"
    :labelAlign="labelAlign"
    contentClass="input-group"
    v-if="loaded"
  >
    <ItpFormInput
      type="number"
      name="billing_frequency_value"
      v-model="billing_frequency.value"
      @input="evh_1485536987289644_input($event, {})"
    >
    </ItpFormInput>
    <DIV
      class="input-group-append w-50"
    >
      <ItpFormSelect
        name="billing_frequency_unit"
        v-model="billing_frequency.unit"
        :options="billingFrequencyOptions"
        @input="evh_6414008351552349_input($event, {})"
      >
      </ItpFormSelect>
    </DIV>
  </ItpFormGroup>
  <ItpFormGroup
    name="payment_terms"
    :label="'Payment period'|pgettext('Contract')"
    labelColsMd="4"
    contentColsMd="6"
    :labelAlign="labelAlign"
    contentClass="input-group"
    v-if="contract.invoicing.payment_terms"
  >
    <ItpFormInput
      type="number"
      name="payment_terms_value"
      v-model="contract.invoicing.payment_terms.value"
    >
    </ItpFormInput>
    <DIV
      class="input-group-append w-50"
    >
      <ItpFormSelect
        name="payment_terms_type"
        v-model="contract.invoicing.payment_terms.type"
        :options="contractPaymentTermTypes"
      >
      </ItpFormSelect>
    </DIV>
  </ItpFormGroup>
  <hr>
  </hr>
  <ItpFormGroup
    name="invoice_notes"
    :label="'Notes on invoice'|pgettext('Contract')"
    labelColsMd="4"
    contentColsMd="8"
    :labelAlign="labelAlign"
  >
    <ItpFormTextarea
      name="invoice_notes_value"
      v-model="contract.invoicing.invoice_notes"
    >
    </ItpFormTextarea>
  </ItpFormGroup>
  <ItpFormGroup
    name="include_contract_number"
    :label="'Contract in invoice notes'|pgettext('Contract')"
    :helpTooltip="'Whether to include the contract number in the invoice notes when generating invoices.'|pgettext('Contract')"
    labelColsMd="4"
    contentColsMd="4"
    :labelAlign="labelAlign"
    v-if="contract.invoicing.invoice_notes_config"
  >
    <ItpFormCheckbox
      name="include_contract_number"
      v-model="contract.invoicing.invoice_notes_config.include_contract_number"
    >
    </ItpFormCheckbox>
  </ItpFormGroup>
  <ItpFormGroup
    name="include_rental"
    :label="'Rental in invoice notes'|pgettext('Contract')"
    :helpTooltip="'Whether to include the rental name in the invoice notes when generating invoices.'|pgettext('Contract')"
    labelColsMd="4"
    contentColsMd="4"
    :labelAlign="labelAlign"
    v-if="contract.invoicing.invoice_notes_config"
  >
    <ItpFormCheckbox
      name="include_rental"
      v-model="contract.invoicing.invoice_notes_config.include_rental"
    >
    </ItpFormCheckbox>
  </ItpFormGroup>
  <ItpFormGroup
    name="include_invoice_period"
    :label="'Period in invoice notes'|pgettext('Contract')"
    :helpTooltip="'Whether to include the billing period in the invoice notes when generating invoices.'|pgettext('Contract')"
    labelColsMd="4"
    contentColsMd="4"
    :labelAlign="labelAlign"
    v-if="contract.invoicing.invoice_notes_config"
  >
    <ItpFormCheckbox
      name="include_invoice_period"
      v-model="contract.invoicing.invoice_notes_config.include_invoice_period"
    >
    </ItpFormCheckbox>
  </ItpFormGroup>
</div>
</template>

<script lang="ts">

import * as _ from 'lodash';
import Big from 'big.js';
import { Vue, Component, Watch, Prop } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import {
  addRoute, UserComponentMixin, ComponentValueCache, actions
} from '@integro/core';

@Component()
export default class FieldsetContractInvoicing extends mixins(UserComponentMixin) {
  componentMetadata = {
    kind: "usercontrol",
  };

  beforeCreate() {
  }

  updateBillingCycle(...args: any[]) {
    this.contract.invoicing.billing_cycle = `${this.billing_frequency.value} ${this.billing_frequency.unit}`
  }

  @Prop({
    required: true,
    type: Object,
  })
  contract!: object;

  @Watch('contract')
  onContract(newValue: any, oldValue: any) {
    if (_.isEqual(newValue, oldValue)) return;
    this.loadDataThrottled("contract")
  }

  @Prop({
    type: String,
    default: "right",
  })
  labelAlign!: string;

  @Watch('labelAlign')
  onLabel_align(newValue: any, oldValue: any) {
    if (_.isEqual(newValue, oldValue)) return;
    this.loadDataThrottled("labelAlign")
  }

  currencies!: any;

  async $$load_currencies() {
    return this.$fn.fetch('list_currency')
  }

  billing_frequency!: any;

  async $$load_billing_frequency() {
    return this.contract.invoicing.billing_cycle != null ?
      {
        value: this.contract.invoicing.billing_cycle.split(" ")[0],
        unit: this.contract.invoicing.billing_cycle.split(" ")[1]
      } : {
        value: null,
        unit: null
      }
  }

  billingFrequencyOptions!: any;
  contractPaymentTermTypes!: any;

  async $$load_contractPaymentTermTypes() {
    return this.$fn.getEnumValuesFromSchema('ContractPaymentTermTypes')
  }

  ux!: any;
  dataMembers = ['currencies', 'billing_frequency', 'billingFrequencyOptions', 'contractPaymentTermTypes', 'ux'];

  data() {
    return {
      ...this.mixinData(),
      actions: this.getActions(),
      ...{
        currencies: null,
        billing_frequency: null,
        billingFrequencyOptions: [{
          value: "day",
          text: this.$fn.gettext("day"),
        }
          ,
        {
          value: "month",
          text: this.$fn.gettext("month"),
        }
          ,
        ]
        ,
        contractPaymentTermTypes: null,
        ux: null,
      },
    }
  }

  async act_1485536987289644_script_8820_getActionArgs_value($event: actions.ActionEvent) {
    // callableMethod act_1485536987289644_script_8820, alias=undefined
    this.updateBillingCycle()
  }

  async act_1485536987289644_script_8820_getActionArgs($event: actions.ActionEvent): Promise<actions.ScriptActionArgs> {
    // parameterResolver name=act_1485536987289644_script_8820, alias=undefined
    return {
      value: () => this.act_1485536987289644_script_8820_getActionArgs_value($event),
    }
  }

  async evh_1485536987289644_input(event: Event, scope: Dictionary<any>) {
    return await this.actions.evh_1485536987289644_input.executeFromDOM(this, event, scope);
  }

  async act_6414008351552349_script_8822_getActionArgs_value($event: actions.ActionEvent) {
    // callableMethod act_6414008351552349_script_8822, alias=undefined
    this.updateBillingCycle()
  }

  async act_6414008351552349_script_8822_getActionArgs($event: actions.ActionEvent): Promise<actions.ScriptActionArgs> {
    // parameterResolver name=act_6414008351552349_script_8822, alias=undefined
    return {
      value: () => this.act_6414008351552349_script_8822_getActionArgs_value($event),
    }
  }

  async evh_6414008351552349_input(event: Event, scope: Dictionary<any>) {
    return await this.actions.evh_6414008351552349_input.executeFromDOM(this, event, scope);
  }

  actions!: {
    act_1485536987289644_script_8820: actions.ScriptAction;
    evh_1485536987289644_input_8819: actions.EventHandlerImpl;
    evh_1485536987289644_input: actions.EventHandlerGroup;
    act_6414008351552349_script_8822: actions.ScriptAction;
    evh_6414008351552349_input_8821: actions.EventHandlerImpl;
    evh_6414008351552349_input: actions.EventHandlerGroup;
  }

  getActions() {
    const act_1485536987289644_script_8820 = new actions.ScriptAction(
      {
        actionArgs: this.act_1485536987289644_script_8820_getActionArgs,
        events: [],
      }
    );
    const evh_1485536987289644_input_8819 = new actions.EventHandlerImpl(
      {
        action: act_1485536987289644_script_8820,
        event: "input",
        displayName: "script",
      }
    );
    const evh_1485536987289644_input = new actions.EventHandlerGroup(
      {
        handlers: [evh_1485536987289644_input_8819],
      }
    );
    const act_6414008351552349_script_8822 = new actions.ScriptAction(
      {
        actionArgs: this.act_6414008351552349_script_8822_getActionArgs,
        events: [],
      }
    );
    const evh_6414008351552349_input_8821 = new actions.EventHandlerImpl(
      {
        action: act_6414008351552349_script_8822,
        event: "input",
        displayName: "script",
      }
    );
    const evh_6414008351552349_input = new actions.EventHandlerGroup(
      {
        handlers: [evh_6414008351552349_input_8821],
      }
    );
    return {
      act_1485536987289644_script_8820,
      evh_1485536987289644_input_8819,
      evh_1485536987289644_input,
      act_6414008351552349_script_8822,
      evh_6414008351552349_input_8821,
      evh_6414008351552349_input,
    }
  }
}

Vue.component("FieldsetContractInvoicing", FieldsetContractInvoicing);

</script>